import React, { useContext, useState } from 'react';
import { AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Church,
  Sparkles,
  Heart,
  GraduationCap,
  ShieldCheck,
  Link2,
  Copy,
  Share2,
  QrCode,
  Users2,
  MousePointerClick,
  Coins,
  Clock,
  CheckCircle2,
  Circle,
  Lock,
  ArrowRight,
  Trophy,
  Repeat,
  Mail,
  TrendingUp,
  UserPlus,
  Award,
  Wallet
} from 'lucide-react';

const REP_REFERRAL = {
  code: 'CHURCH-FBC',
  url: 'consistency.app/cp/CHURCH-FBC'
};

const REP_METRICS = {
  churchesReferred: 12,
  conversions: 4,
  pending: '$240',
  paid: '$1,180'
};

const PROGRAM_REQUIREMENTS = [
  { key: 'training', label: 'Complete sales training' },
  { key: 'standing', label: 'Maintain good standing' },
  { key: 'cert', label: 'Certification approval required' },
  { key: 'compliance', label: 'Faith-based compliance' }
];

const STEPS = [
  { n: 1, t: 'Apply to become a Church Partner Rep', icon: Mail },
  { n: 2, t: 'Complete required sales training', icon: GraduationCap },
  { n: 3, t: 'Get certified by Church Partner Lead', icon: ShieldCheck },
  { n: 4, t: 'Refer churches to Church Pro', icon: Church },
  { n: 5, t: 'Earn recurring commissions', icon: Coins }
];

const HOW_EARN = [
  { n: 1, t: 'Share referral link', icon: Link2 },
  { n: 2, t: 'Church subscribes', icon: Church },
  { n: 3, t: 'Revenue clears verification', icon: ShieldCheck },
  { n: 4, t: 'Monthly payout issued', icon: Coins }
];

const LEAD_PIPELINE = [
  { stage: 'Applied', count: 18, accent: 'cyan' },
  { stage: 'Training', count: 9, accent: 'blue' },
  { stage: 'Pending Certification', count: 4, accent: 'amber' },
  { stage: 'Certified', count: 27, accent: 'green' },
  { stage: 'Suspended', count: 2, accent: 'red' }
];

const REP_PERFORMANCE_ROWS = [
  { name: 'Faith B. Church', referrals: 14, signups: 5, conversions: 4, earnings: '$320' },
  { name: 'Grace Network', referrals: 11, signups: 3, conversions: 3, earnings: '$240' },
  { name: 'Living Word Ministry', referrals: 8, signups: 2, conversions: 2, earnings: '$160' },
  { name: 'Cornerstone Collective', referrals: 6, signups: 2, conversions: 1, earnings: '$80' }
];

// ---------------- Reusable ----------------
const StatusPill = ({ status }) => {
  const map = {
    not_joined: { label: 'Not Joined', cls: 'border-white/15 text-[#C7CED9] bg-white/[0.03]' },
    applied: { label: 'Applied', cls: 'border-cyan-500/30 text-cyan-300 bg-cyan-500/10' },
    invited: { label: 'Invited', cls: 'border-blue-500/30 text-blue-300 bg-blue-500/10' },
    training: { label: 'Training In Progress', cls: 'border-blue-500/30 text-blue-300 bg-blue-500/10' },
    pending: { label: 'Pending Certification', cls: 'border-amber-500/30 text-amber-300 bg-amber-500/10' },
    certified: { label: 'Certified Rep', cls: 'border-green-500/30 text-green-300 bg-green-500/10' },
    lead: { label: 'Church Partner Lead', cls: 'border-pink-500/30 text-pink-300 bg-pink-500/10' }
  };
  const e = map[status] || map.not_joined;
  return (
    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${e.cls}`}>
      {e.label}
    </Badge>
  );
};

const StatCard = ({ icon: Icon, accent, label, value, testId }) => (
  <Card className="glass border-white/5" data-testid={testId}>
    <CardContent className="p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-6 h-6 rounded-md bg-${accent}-500/10 border border-white/5 flex items-center justify-center`}>
          <Icon className={`h-3 w-3 text-${accent}-400`} />
        </div>
        <p className="text-[10px] uppercase tracking-wider text-[#8A93A6]">{label}</p>
      </div>
      <p className="text-xl font-bold text-white tabular-nums leading-tight">{value}</p>
    </CardContent>
  </Card>
);

const ShareWinPill = ({ accent = 'amber', preview, testId }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid={testId}>
    <button
      type="button"
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-${accent}-500/10 border border-${accent}-500/25 text-[10px] font-medium text-${accent}-200 hover:bg-${accent}-500/15 transition-colors`}
    >
      <Share2 className="h-3 w-3" />
      Share Win
    </button>
    {preview && (
      <span className="text-[10px] text-[#8A93A6] truncate flex-1 min-w-0 italic">“{preview}”</span>
    )}
  </div>
);

const SectionHeader = ({ icon: Icon, accent = 'amber', title, hint }) => (
  <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
    <div>
      <h2 className="text-base font-semibold text-white flex items-center gap-2">
        {Icon && <Icon className={`h-4 w-4 text-${accent}-400`} />}
        {title}
      </h2>
      {hint && <p className="text-[11px] text-[#8A93A6]">{hint}</p>}
    </div>
  </div>
);

// ============================================================
// MAIN
// ============================================================
export default function ChurchPartner() {
  const { user } = useContext(AuthContext);
  const isAdmin = !!user?.is_internal_admin;

  // State selector — Admin can preview every state; non-admin defaults to not_joined
  const [state, setState] = useState(isAdmin ? 'certified' : 'not_joined');
  const [leadView, setLeadView] = useState(isAdmin); // admin preview defaults to lead view

  const handleCopy = () => {
    if (navigator?.clipboard) navigator.clipboard.writeText(REP_REFERRAL.url).catch(() => {});
    toast.success('Referral link copied');
  };

  const showNotJoined = state === 'not_joined';
  const showTraining = state === 'training' || state === 'pending';
  const showCertified = state === 'certified';

  return (
    <div className="container mx-auto p-6 max-w-5xl" data-testid="church-partner-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <Church className="h-6 w-6 text-amber-400" />
          <h1 className="text-3xl font-bold text-white">Church Partner</h1>
          <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-300 bg-amber-500/10 uppercase tracking-wider font-semibold">
            Community Network
          </Badge>
          <Badge variant="outline" className="text-[10px] border-violet-500/30 text-violet-300 bg-violet-500/10 uppercase tracking-wider font-semibold">
            Faith-Based Program
          </Badge>
          {isAdmin && (
            <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider font-semibold">
              Admin Preview
            </Badge>
          )}
        </div>
        <p className="text-[#8A93A6] max-w-2xl">
          Faith-based creator growth and church partnerships.
        </p>
      </div>

      {/* Admin Preview state switcher */}
      {isAdmin && (
        <div
          className="mb-6 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 flex flex-wrap items-center gap-3"
          data-testid="church-state-switcher"
        >
          <span className="text-[10px] uppercase tracking-wider text-[#8A93A6]">Admin · Preview State</span>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="h-8 w-[220px] bg-[#1C1F26] border-white/10 text-white text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not_joined">State 1 — Not Joined</SelectItem>
              <SelectItem value="training">State 2 — Training In Progress</SelectItem>
              <SelectItem value="pending">State 2 — Pending Certification</SelectItem>
              <SelectItem value="certified">State 3 — Certified Rep Dashboard</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2 ml-auto">
            <span className="text-[10px] uppercase tracking-wider text-[#8A93A6]">Lead View</span>
            <button
              type="button"
              onClick={() => setLeadView(!leadView)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] border transition-colors ${
                leadView
                  ? 'border-pink-500/30 text-pink-300 bg-pink-500/10'
                  : 'border-white/10 text-[#8A93A6] bg-white/[0.02] hover:text-white'
              }`}
              data-testid="lead-view-toggle"
            >
              <ShieldCheck className="h-3 w-3" />
              {leadView ? 'On' : 'Off'}
            </button>
          </div>
        </div>
      )}

      {/* Partner Status (always visible) */}
      <Card className="glass border-white/5 mb-6" data-testid="partner-status-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-white/5 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="text-sm font-semibold text-white">Partner Status</p>
                  <StatusPill status={state} />
                  {leadView && isAdmin && showCertified && <StatusPill status="lead" />}
                </div>
                <p className="text-[11px] text-[#8A93A6] max-w-lg leading-relaxed">
                  {showNotJoined && 'Faith-based partner program for churches, ministries, and creator collectives.'}
                  {state === 'training' && 'Complete the sales training to move toward certification.'}
                  {state === 'pending' && 'Awaiting certification approval from your Church Partner Lead.'}
                  {showCertified && 'You are certified. Share your link to begin earning recurring commissions.'}
                </p>
              </div>
            </div>

            {showNotJoined && !isAdmin && (
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" data-testid="invite-accept-btn">
                  <Mail className="h-4 w-4 mr-2" />
                  Accept Invitation
                </Button>
                <Button className="bg-amber-500 hover:bg-amber-600 text-[#1a1208]" data-testid="apply-btn">
                  Apply to Join Program
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ============================================================
          STATE 1 — NOT JOINED
         ============================================================ */}
      {showNotJoined && (
        <>
          {/* Hero */}
          <Card className="glass border-amber-500/15 ring-1 ring-amber-500/10 mb-6 relative overflow-hidden" data-testid="hero-card">
            <div className="absolute -top-16 -right-12 w-40 h-40 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
            <CardContent className="p-5 relative">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-11 h-11 rounded-2xl bg-amber-500/10 border border-white/10 flex items-center justify-center">
                  <Church className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-lg font-bold text-white">Church Partner Program</p>
                  <p className="text-sm text-[#8A93A6] leading-relaxed max-w-xl">
                    Help churches, ministries, and faith communities empower creators using Consistency.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                {[
                  { label: 'Faith-based community growth', icon: Heart, accent: 'rose' },
                  { label: 'Recurring commissions', icon: Coins, accent: 'amber' },
                  { label: 'Church Pro partnerships', icon: Church, accent: 'pink' },
                  { label: 'Training + certification', icon: GraduationCap, accent: 'cyan' }
                ].map((h) => {
                  const HIcon = h.icon;
                  return (
                    <div
                      key={h.label}
                      className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-2"
                    >
                      <HIcon className={`h-3.5 w-3.5 text-${h.accent}-400 shrink-0`} />
                      <span className="text-[11px] text-[#C7CED9]">{h.label}</span>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button className="bg-amber-500 hover:bg-amber-600 text-[#1a1208]" data-testid="hero-apply-btn">
                  Apply to Join Program
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button variant="outline" data-testid="hero-accept-invite-btn">
                  <Mail className="h-4 w-4 mr-2" />
                  Received an Invite? Accept Invitation
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* How it Works (5 steps) */}
          <section className="mb-6" data-testid="how-it-works-section">
            <SectionHeader icon={Repeat} title="How It Works" hint="From application to commissions." />
            <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {STEPS.map((s) => {
                const SIcon = s.icon;
                return (
                  <li
                    key={s.n}
                    className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2.5"
                    data-testid={`how-step-${s.n}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[9px] uppercase tracking-wider text-amber-300 font-semibold tabular-nums">
                        Step {s.n}
                      </span>
                      <SIcon className="h-3 w-3 text-[#8A93A6]" />
                    </div>
                    <p className="text-xs text-white leading-snug">{s.t}</p>
                  </li>
                );
              })}
            </ol>
          </section>

          {/* Compensation */}
          <section className="mb-6" data-testid="compensation-section">
            <SectionHeader icon={Coins} title="Compensation Structure" hint="Two roles. Two reward systems." />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="glass border-white/5" data-testid="comp-rep-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-white/5 flex items-center justify-center">
                      <Users2 className="h-4 w-4 text-cyan-400" />
                    </div>
                    <p className="text-sm font-semibold text-white">Church Partner Rep</p>
                  </div>
                  <p className="text-[11px] text-[#8A93A6] leading-relaxed mb-3">
                    Earn recurring commissions through Church Pro referrals. Same compensation structure as the Affiliate Program.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    <Badge variant="outline" className="text-[10px] border-white/15 text-[#C7CED9] bg-white/[0.03] uppercase tracking-wider">
                      Monthly payouts
                    </Badge>
                    <Badge variant="outline" className="text-[10px] border-white/15 text-[#C7CED9] bg-white/[0.03] uppercase tracking-wider">
                      Stripe payouts
                    </Badge>
                    <Badge variant="outline" className="text-[10px] border-white/15 text-[#C7CED9] bg-white/[0.03] uppercase tracking-wider">
                      Recurring commissions
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass border-pink-500/20 ring-1 ring-pink-500/10" data-testid="comp-lead-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-pink-500/10 border border-white/5 flex items-center justify-center">
                      <ShieldCheck className="h-4 w-4 text-pink-400" />
                    </div>
                    <p className="text-sm font-semibold text-white">Church Partner Lead</p>
                    <Badge variant="outline" className="ml-auto text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider">
                      Only one
                    </Badge>
                  </div>
                  <p className="text-[11px] text-[#8A93A6] leading-relaxed mb-2">
                    Lead, train, and manage Church Partner Program growth.
                  </p>
                  <div className="rounded-md border border-pink-500/15 bg-pink-500/[0.04] px-3 py-2 mb-2">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xl font-bold text-pink-300 tabular-nums">10%</span>
                      <span className="text-[11px] text-[#C7CED9]">of Net Church Partner Program Revenue</span>
                    </div>
                  </div>
                  <p className="text-[11px] text-[#8A93A6]">Lead can also participate as a Rep and earn rep commissions.</p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Requirements */}
          <section data-testid="requirements-section">
            <SectionHeader icon={CheckCircle2} title="Program Requirements" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PROGRAM_REQUIREMENTS.map((r) => (
                <div
                  key={r.key}
                  className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] px-3 py-2"
                  data-testid={`req-${r.key}`}
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                  <span className="text-[12px] text-white">{r.label}</span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {/* ============================================================
          STATE 2 — TRAINING / PENDING
         ============================================================ */}
      {showTraining && (
        <>
          <Card className="glass border-white/5 mb-4" data-testid="training-status-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <GraduationCap className="h-4 w-4 text-amber-400" />
                <p className="text-sm font-semibold text-white">Sales Training Progress</p>
                <Badge variant="outline" className="ml-auto text-[10px] border-amber-500/30 text-amber-300 bg-amber-500/10 uppercase tracking-wider">
                  {state === 'training' ? '4 of 8 modules' : '8 of 8 modules'}
                </Badge>
              </div>
              <Progress value={state === 'training' ? 50 : 100} className="h-1.5 bg-white/[0.04]" />
              <p className="text-[11px] text-[#8A93A6] mt-2">
                {state === 'training'
                  ? 'Continue completing modules. Once complete, your application moves to Pending Certification.'
                  : 'Training complete. Awaiting final certification approval from your Church Partner Lead.'}
              </p>
            </CardContent>
          </Card>

          <Card className="glass border-white/5 mb-4" data-testid="locked-referral-card">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/5 flex items-center justify-center">
                  <Lock className="h-4 w-4 text-[#8A93A6]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white">Referral Access Locked</p>
                  <p className="text-[11px] text-[#8A93A6] leading-relaxed">
                    Referral access unlocks after certification. Your link will appear here once your Church Partner Lead approves you.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Re-use How it Works as orientation */}
          <section data-testid="training-how-section">
            <SectionHeader icon={Repeat} title="What Happens Next" hint="Path from training to first payout." />
            <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {STEPS.map((s) => {
                const SIcon = s.icon;
                const done = s.n <= (state === 'training' ? 1 : 2);
                return (
                  <li
                    key={s.n}
                    className={`rounded-md border px-3 py-2.5 ${
                      done ? 'border-green-500/25 bg-green-500/[0.05]' : 'border-white/5 bg-white/[0.02]'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {done ? (
                        <CheckCircle2 className="h-3 w-3 text-green-400" />
                      ) : (
                        <Circle className="h-3 w-3 text-[#555E6E]" />
                      )}
                      <span className="text-[9px] uppercase tracking-wider text-amber-300 font-semibold tabular-nums">
                        Step {s.n}
                      </span>
                      <SIcon className="h-3 w-3 text-[#8A93A6] ml-auto" />
                    </div>
                    <p className={`text-xs leading-snug ${done ? 'text-white' : 'text-[#8A93A6]'}`}>{s.t}</p>
                  </li>
                );
              })}
            </ol>
          </section>
        </>
      )}

      {/* ============================================================
          STATE 3 — CERTIFIED REP DASHBOARD
         ============================================================ */}
      {showCertified && (
        <>
          {/* Referral Link */}
          <Card className="glass border-white/5 mb-4" data-testid="referral-link-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="h-4 w-4 text-amber-400" />
                <p className="text-sm font-semibold text-white">Your Referral Link</p>
                <Badge variant="outline" className="ml-auto text-[10px] border-green-500/30 text-green-300 bg-green-500/10 uppercase tracking-wider">
                  Certified by Lead
                </Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-stretch">
                <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 flex items-center justify-between">
                  <span className="text-sm text-[#C7CED9] font-mono truncate">{REP_REFERRAL.url}</span>
                  <Badge variant="outline" className="text-[10px] border-white/15 text-[#8A93A6] bg-white/[0.03] uppercase tracking-wider ml-2 shrink-0">
                    Code: {REP_REFERRAL.code}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={handleCopy} data-testid="copy-referral-btn">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button className="bg-amber-500 hover:bg-amber-600 text-[#1a1208]" data-testid="share-referral-btn">
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 text-[11px] text-[#8A93A6]">
                <div className="w-9 h-9 rounded border border-white/10 bg-white/[0.02] flex items-center justify-center">
                  <QrCode className="h-4 w-4 text-[#8A93A6]" />
                </div>
                <span>QR code available for church bulletins, services & events (coming soon).</span>
              </div>
            </CardContent>
          </Card>

          {/* Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="rep-metrics">
            <StatCard icon={Church} accent="amber" label="Churches Referred" value={REP_METRICS.churchesReferred} testId="stat-churches" />
            <StatCard icon={MousePointerClick} accent="cyan" label="Conversions" value={REP_METRICS.conversions} testId="stat-conversions" />
            <StatCard icon={Clock} accent="amber" label="Pending Earnings" value={REP_METRICS.pending} testId="stat-pending" />
            <StatCard icon={Coins} accent="green" label="Paid Earnings" value={REP_METRICS.paid} testId="stat-paid" />
          </div>

          {/* Payout Structure */}
          <Card className="glass border-white/5 mb-4" data-testid="payout-structure-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Wallet className="h-4 w-4 text-cyan-400" />
                <p className="text-sm font-semibold text-white">Payout Structure</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-2">
                  <p className="text-[9px] uppercase tracking-wider text-[#8A93A6]">Type</p>
                  <p className="text-xs text-white">Recurring commissions</p>
                </div>
                <div className="rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-2">
                  <p className="text-[9px] uppercase tracking-wider text-[#8A93A6]">Cadence</p>
                  <p className="text-xs text-white">Monthly payouts</p>
                </div>
                <div className="rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-2">
                  <p className="text-[9px] uppercase tracking-wider text-[#8A93A6]">Verification Window</p>
                  <p className="text-xs text-white">Pending until cleared revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shareable Wins */}
          <Card className="glass border-white/5 mb-4" data-testid="shareable-wins-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="h-4 w-4 text-amber-400" />
                <p className="text-sm font-semibold text-white">Shareable Wins</p>
              </div>
              <div className="space-y-1.5">
                <ShareWinPill preview="We just helped another church empower creators." testId="win-empower" />
                <ShareWinPill accent="rose" preview="Another ministry joined the Consistency ecosystem." testId="win-ministry" />
                <ShareWinPill accent="cyan" preview="Our church network continues growing." testId="win-network" />
              </div>
            </CardContent>
          </Card>

          {/* How Earnings Work */}
          <Card className="glass border-white/5 mb-6" data-testid="how-earnings-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Repeat className="h-4 w-4 text-violet-400" />
                <p className="text-sm font-semibold text-white">How Earnings Work</p>
              </div>
              <ol className="grid grid-cols-1 md:grid-cols-4 gap-2">
                {HOW_EARN.map((s) => {
                  const SIcon = s.icon;
                  return (
                    <li
                      key={s.n}
                      className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2"
                      data-testid={`earn-step-${s.n}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[9px] uppercase tracking-wider text-amber-300 font-semibold tabular-nums">
                          Step {s.n}
                        </span>
                        <SIcon className="h-3 w-3 text-[#8A93A6]" />
                      </div>
                      <p className="text-xs text-white leading-snug">{s.t}</p>
                    </li>
                  );
                })}
              </ol>
            </CardContent>
          </Card>

          {/* ============================================================
              STATE 4 — Lead Dashboard (Admin Preview)
             ============================================================ */}
          {isAdmin && leadView && (
            <section data-testid="lead-dashboard-section">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-pink-400" />
                  <h2 className="text-base font-semibold text-white">Church Partner Lead Dashboard</h2>
                  <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider">
                    Lead View
                  </Badge>
                </div>
                <Button variant="outline" size="sm" data-testid="invite-rep-btn">
                  <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                  Invite Rep
                </Button>
              </div>

              {/* Rep Pipeline */}
              <Card className="glass border-white/5 mb-4" data-testid="rep-pipeline-card">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold text-white mb-3">Rep Pipeline</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {LEAD_PIPELINE.map((p) => (
                      <div
                        key={p.stage}
                        className={`rounded-md border border-${p.accent}-500/20 bg-${p.accent}-500/[0.04] px-2.5 py-2`}
                        data-testid={`pipeline-${p.stage.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <p className="text-[9px] uppercase tracking-wider text-[#8A93A6] mb-0.5">{p.stage}</p>
                        <p className={`text-xl font-bold text-${p.accent}-300 tabular-nums leading-tight`}>{p.count}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Rep Performance */}
              <Card className="glass border-white/5 mb-4" data-testid="rep-performance-card">
                <CardContent className="p-0">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                    <p className="text-sm font-semibold text-white">Rep Performance</p>
                    <Badge variant="outline" className="text-[10px] border-white/15 text-[#8A93A6] bg-white/[0.03] uppercase tracking-wider">
                      Top reps · this month
                    </Badge>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-[#8A93A6] border-b border-white/5">
                          <th className="text-left font-medium px-4 py-2">Rep / Church</th>
                          <th className="text-right font-medium px-3 py-2">Referrals</th>
                          <th className="text-right font-medium px-3 py-2">Signups</th>
                          <th className="text-right font-medium px-3 py-2">Conversions</th>
                          <th className="text-right font-medium px-4 py-2">Earnings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {REP_PERFORMANCE_ROWS.map((r) => (
                          <tr key={r.name} className="border-b border-white/[0.03] last:border-0">
                            <td className="px-4 py-2.5 text-white">{r.name}</td>
                            <td className="px-3 py-2.5 text-right text-[#C7CED9] tabular-nums">{r.referrals}</td>
                            <td className="px-3 py-2.5 text-right text-[#C7CED9] tabular-nums">{r.signups}</td>
                            <td className="px-3 py-2.5 text-right text-[#C7CED9] tabular-nums">{r.conversions}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-amber-300 tabular-nums">{r.earnings}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Lead Revenue */}
              <Card className="glass border-pink-500/20 ring-1 ring-pink-500/10" data-testid="lead-revenue-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-pink-400" />
                      <p className="text-sm font-semibold text-white">Lead Revenue</p>
                      <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider">
                        10% Override
                      </Badge>
                    </div>
                    <span className="text-[11px] text-[#8A93A6]">
                      <TrendingUp className="h-3 w-3 inline mr-1 text-green-400" />
                      Tracking active
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2">
                      <p className="text-[9px] uppercase tracking-wider text-[#8A93A6]">Net Program Revenue (MTD)</p>
                      <p className="text-xl font-bold text-white tabular-nums">$18,420</p>
                    </div>
                    <div className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2">
                      <p className="text-[9px] uppercase tracking-wider text-[#8A93A6]">Lead Override (10%)</p>
                      <p className="text-xl font-bold text-pink-300 tabular-nums">$1,842</p>
                    </div>
                    <div className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2">
                      <p className="text-[9px] uppercase tracking-wider text-[#8A93A6]">+ Personal Rep Earnings</p>
                      <p className="text-xl font-bold text-amber-300 tabular-nums">$420</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          )}
        </>
      )}
    </div>
  );
}
